import { useAlert } from "hooks/useAlert";
import { Form, Formik, FormikConfig, FormikHelpers, FormikValues } from "formik";
import { api } from "services/api.service";
import { AxiosRequestConfig, AxiosResponse, Method } from "axios";
import { ApiError } from "types/apiError";
import { isAxiosError } from "utils/helpers";
import { FormHTMLAttributes, ReactNode } from "react";

function getNamespacedKey(namespace: string | undefined, key: string) {
  if (namespace) {
    return `${namespace}[${key}]`;
  } else {
    return key;
  }
}

function convertObjectToFormData(obj: FormikValues, formData: FormData, namespace?: string) {
  for (const [key, val] of Object.entries(obj)) {
    if (val instanceof File) {
      formData.append(getNamespacedKey(namespace, key), val);
    } else if (val instanceof Array) {
      val.forEach((item, i) => {
        convertObjectToFormData(item, formData, `${getNamespacedKey(namespace, key)}[${i}]`);
      });
    } else if (val instanceof Object) {
      convertObjectToFormData(val, formData, namespace ? `${namespace}[${key}]` : key);
    } else {
      formData.append(getNamespacedKey(namespace, key), val);
    }
  }
}

function convertToFormData(data: FormikValues) {
  const formData = new FormData();
  convertObjectToFormData(data, formData);
  return formData;
}

interface RevivnApiFormProps<Values extends FormikValues, Response> {
  initialValues: Values;
  action: string;
  method: Method;
  children: ReactNode;
  enctype?: string;
  onSubmit?: (data: Values, formikBag: FormikHelpers<Values>) => void;
  onSettled?: (formikBag: FormikHelpers<Values>) => void;
  onError?: (error: ApiError) => void;
  onSuccess?: (response: AxiosResponse<Response>) => void;
  FormProps?: FormHTMLAttributes<HTMLFormElement>;
  FormikProps?: Partial<FormikConfig<Values>>;
  validationSchema?: FormikConfig<Values>["validationSchema"];
  enableReinitialize?: boolean;
}

export function RevivnApiForm<Values extends FormikValues, Response>(props: RevivnApiFormProps<Values, Response>) {
  const { alertError } = useAlert();

  return (
    <Formik<Values>
      initialValues={props.initialValues}
      onSubmit={async (data: Values, formikBag: FormikHelpers<Values>) => {
        props.onSubmit?.(data, formikBag);
        const additionalConfig: AxiosRequestConfig = {};
        if (props.enctype === "multipart/form-data") {
          additionalConfig.headers = { "Content-Type": "multipart/form-data" };
          additionalConfig.data = convertToFormData(data);
        }
        try {
          const result = await api.request<Response>({
            url: props.action,
            method: props.method,
            data: data,
            ...additionalConfig,
          });
          props.onSuccess?.(result);
        } catch (err) {
          if (!isAxiosError(err)) throw err;
          props.onError?.(err);
          const response = err.response;
          const errors = response?.data?.errors;
          if (errors && response?.status === 422) {
            if (Array.isArray(errors)) {
              const parsedErrors = errors.map((message) => {
                try {
                  const parsed = JSON.parse(message);
                  return Array.isArray(parsed) ? parsed.map((e) => e.message).join("\n") : parsed.message || message;
                } catch {
                  return message;
                }
              });
              alertError(parsedErrors.join("\n"));
            } else if (errors instanceof Object) {
              for (const [key, val] of Object.entries(errors as Record<string, string>)) {
                formikBag.setFieldError(key, val);
                formikBag.setFieldTouched(key, true, false);
              }
            }
          } else if (!props.onError) {
            alertError(err.message);
          }
        }
        props.onSettled?.(formikBag);
      }}
      validationSchema={props.validationSchema}
      enableReinitialize={props.enableReinitialize}
      {...props.FormikProps}
    >
      <Form action={props.action} method={props.method} {...props.FormProps}>
        {props.children}
      </Form>
    </Formik>
  );
}
